import { useMemo, useState } from "react";
import { FbaBox, useShipmentContext } from "@/contexts/ShipmentContext";

import styles from "./BulkDimensionsAndWeightModal.module.css";
import { updateBoxV2 } from "@/api/fulfillment";
import { DimensionsAndWeight, getDefaultDimensionsAndWeight } from "../utils";
import DimensionsAndWeightRow, { equals } from "../DimensionsAndWeightRow/DimensionsAndWeightRow";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import Flex from "@/components/Flex/Flex";
import Button from "@/components/forms/Button/Button";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import IconBox from "@/components/icons/IconBox";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { LocalStorageKeys, setLocalStorageItem } from "@/utils/localStorageUtils";
import { CreateNewBoxButton } from "../CreateNewBoxButton/CreateNewBoxButton";

export interface BulkEditDimensionAndWeightModalProps {
  boxes: FbaBox[];
  onClose: Function;
  open: boolean;
  createBoxes: (n: number) => Promise<void>;
}

const boxToDimensionsAndWeight = (box: FbaBox): DimensionsAndWeight => {
  return {
    length: box.length,
    width: box.width,
    height: box.height,
    weight: box.weight,
  };
}

export default function BulkEditDimensionAndWeightModal(props: BulkEditDimensionAndWeightModalProps) {
  const { setCurrentBatch, currentBatch } = useShipmentContext();
  const { boxes = [], onClose, open } = props;

  const [applyToAllValue, setApplyToAllValue] = useState<DimensionsAndWeight>(getDefaultDimensionsAndWeight());
  const [loading, setLoading] = useState<boolean>(false);

  const [allBoxesChecked, setAllBoxesChecked] = useState<boolean>(true);
  const [selectedBoxes, setSelectedBoxes] = useState<boolean[]>(boxes.map(b => !!b));
  const [desiredBoxDimensions, setDesiredBoxDimensions] = useState<DimensionsAndWeight[]>(boxes.map(boxToDimensionsAndWeight));

  useMemo(() => {
    setSelectedBoxes(boxes.map(b => !!b));
    setDesiredBoxDimensions(boxes.map(boxToDimensionsAndWeight));
  }, [boxes, open])

  return (
    <Modal
      className={styles.modal}
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <ModalHeader>
        Bulk Dimensions and Weight
      </ModalHeader>

      <ModalBody>
        <Flex direction="column" className={styles.bulkDimsModal}>
          <Flex className={styles.topRow} align="end" gap={2}>
            <DimensionsAndWeightRow
              showMaxWeightAndDimensionsTooltip
              value={applyToAllValue}
              onChange={e => setApplyToAllValue({ ...e })}
            />
            <Button
              type="primary"
              disabled={boxes.length === 0}
              className={styles.applyToAllButton}
              onClick={() => {
                setDesiredBoxDimensions(desiredBoxDimensions.map(() => ({ ...applyToAllValue })));
              }}
            >
              Apply to all
            </Button>
          </Flex>

          {boxes.length > 0 && (
            <Flex gap={2} align="center">
              <label className={styles.allBoxesCheckedLabel}>
                <Checkbox
                  id="selectAll"
                  value={allBoxesChecked}
                  onChange={(e) => {
                    if (allBoxesChecked) {
                      setSelectedBoxes([...selectedBoxes.map(b => false)]);

                    } else {
                      setSelectedBoxes([...selectedBoxes.map(b => true)]);
                    }

                    setAllBoxesChecked(!allBoxesChecked);
                  }}
                />
                {allBoxesChecked ? "De-select all boxes" : "Select all boxes"}
              </label>
            </Flex>
          )}
          {boxes.map((box, index) => (
            <Flex className={styles.boxEditRow} key={index} align="end" gap={2}>
              <Flex align="center" gap={1} style={{whiteSpace: "nowrap"}}>
                <Checkbox
                  value={selectedBoxes[index]}
                  onChange={(e) => {
                    selectedBoxes[index] = !selectedBoxes[index];
                    setSelectedBoxes([...selectedBoxes]);
                  }}
                />
                <IconBox />
                <h3 style={{whiteSpace: "nowrap", marginRight: "12px"}}>Box {box.displayIndex}</h3>
              </Flex>
              <DimensionsAndWeightRow
                value={selectedBoxes[index] ? desiredBoxDimensions[index] : { weight: box.weight, width: box.width, length: box.length, height: box.height }}
                onChange={(e) => {
                  desiredBoxDimensions[index] = e;
                  setDesiredBoxDimensions([...desiredBoxDimensions]);
                }}
              />
            </Flex>
          ))}
        </Flex>
      </ModalBody>

      <ModalFooter>
        {loading
          ? <Flex justify="end"><LoadingSpinner /></Flex>
          : <Flex gap={2} justify="space-between">
            <CreateNewBoxButton createBoxes={props.createBoxes} />

            <Flex gap={2}>
              <Button type="secondary" onClick={() => onClose()}>Cancel</Button>
              <Button
                disabled={selectedBoxes.filter(sb => sb).length === 0}
                onClick={() => {
                  setLoading(true);
                  const requestBoxes: { box: FbaBox, requestedDimensionsAndWeight: DimensionsAndWeight }[] = [];
                  selectedBoxes.forEach((selected, i) => {
                    if (selected) {
                      requestBoxes.push({ box: boxes[i], requestedDimensionsAndWeight: desiredBoxDimensions[i] });
                    }
                  });
                  const dimsAndWeight0 = requestBoxes[0].requestedDimensionsAndWeight;
                  let allDimsAndWeightTheSame = true;
                  for (let i = 1; i < requestBoxes.length; i++) {
                    if (!equals(requestBoxes[i].requestedDimensionsAndWeight, dimsAndWeight0)) {
                      allDimsAndWeightTheSame = false;
                      break;
                    }
                  }

                  Promise.all(requestBoxes.map((rb) => {
                    return updateBoxV2(rb.box.id, {
                      items: rb.box.packingItems,
                      width: rb.requestedDimensionsAndWeight.width.toString(),
                      height: rb.requestedDimensionsAndWeight.height.toString(),
                      length: rb.requestedDimensionsAndWeight.length.toString(),
                      weight: rb.requestedDimensionsAndWeight.weight.toString(),
                      packingGroupId: rb.box.packingGroupId,
                    });
                  }))
                    .then(_ => {
                      requestBoxes.forEach((rb) => {
                        Object.assign(rb.box, rb.requestedDimensionsAndWeight);
                      });
                      if (allDimsAndWeightTheSame) setLocalStorageItem(LocalStorageKeys.lastUsedDimensionsAndWeight, dimsAndWeight0);
                      setCurrentBatch({ ...currentBatch });
                      onClose();
                      setLoading(false);
                    })
                    .catch(reason => {
                      console.error(reason);
                      setLoading(false);
                    });
                }}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        }
      </ModalFooter>
    </Modal>
  );
}